import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { AppDataKeyField } from '@/form/AppDataKeyField';
import {
    AppCheckboxField,
    AppInfoTypeSelectField,
    AppInputField,
    AppPromptsField,
    AppSelectField,
} from '@/form/FormUI';
import type { InputNodeType, NodeFormProps } from '@/nodes';
import { infoTypesState, useSavedFormValues } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputDataSchema, type InputDataType, isSelectField } from '@rock/types';
import { type FC, useCallback, useEffect } from 'react';
import { type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

type FormInput = z.input<typeof InputDataSchema>;

const InputForm: FC<NodeFormProps<InputNodeType>> = ({ spaceId, flowId, node, updateNode, cancelUpdate }) => {
    const infoTypes = useAsyncAtomValue(infoTypesState(spaceId));
    const [formState, setFormState] = useSavedFormValues<FormInput>({ flowId, nodeId: node.id });
    const form = useForm<FormInput, unknown, InputDataType>({
        resolver: zodResolver(InputDataSchema),
        defaultValues: formState || node.data,
    });

    const watchedFields = useWatch({ control: form.control });
    useEffect(() => {
        setFormState(watchedFields);
    }, [setFormState, watchedFields]);

    // A select info type renders as tap-to-commit option buttons in the app, so the placeholder and
    // continue button have nothing to drive.
    const selectedInfoType = infoTypes?.find((t) => t.id === watchedFields.infoTypeId);
    const isSelectInfoType = !!selectedInfoType && isSelectField(selectedInfoType);

    const onSubmit = useCallback(
        (values: InputDataType) => {
            setFormState(null);
            updateNode({
                ...node,
                data: values,
            });
        },
        [setFormState, node, updateNode],
    );

    const onInvalid = useCallback((errors: FieldErrors) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        setFormState(null);
        cancelUpdate();
    }, [setFormState, cancelUpdate]);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                className="flex flex-1 flex-col gap-8 items-stretch overflow-hidden"
            >
                <div className="flex flex-1 flex-col gap-8 items-stretch p-5 overflow-scroll">
                    <AppInputField
                        label="Page Title"
                        placeholder="Page Title"
                        control={form.control}
                        name="pageTitle"
                        description="The title displayed in the navigation header."
                    />

                    <AppPromptsField description="What message are we showing the user?" />

                    <AppDataKeyField
                        control={form.control}
                        name="dataKey"
                        label="Data Key"
                        placeholder="field_name"
                        description="The name used to reference the user-provided value in code."
                        mode="produce"
                        currentNodeId={node.id}
                    />

                    <AppInfoTypeSelectField
                        control={form.control}
                        name="infoTypeId"
                        label="Info Type"
                        description="Which info type are we capturing on this screen?"
                        infoTypes={infoTypes}
                        placeholder="Select Info Type"
                    />

                    {!isSelectInfoType && (
                        <AppInputField
                            label="Placeholder"
                            placeholder="Enter value here..."
                            control={form.control}
                            name="placeholder"
                            description="(Optional) The value to show in the field before anything is entered."
                        />
                    )}

                    <AppCheckboxField
                        label="Allow Skip"
                        control={form.control}
                        name="optional"
                        description={
                            isSelectInfoType
                                ? 'If true, we add a "Not Sure" option the user can pick to move on.'
                                : 'If true, we will allow the user to continue without entering a value.'
                        }
                    />

                    <AppSelectField
                        label="Help Type"
                        control={form.control}
                        name="helpType"
                        description="Help Me Decide is only available for select info types."
                        options={['help-me-decide', 'none']}
                    />

                    {!isSelectInfoType && (
                        <AppInputField
                            label="Button Label"
                            placeholder="Continue"
                            control={form.control}
                            name="buttonLabel"
                            description="The label on the button to continue."
                        />
                    )}
                </div>

                <div className="flex flex-row gap-3 p-5">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};

export default InputForm;
